import { useSelector } from 'react-redux';

import type { ProductFeatureItem } from 'src/components/molecules/ProductFeatures/components/ProductFeatures';
import { ProductFeature } from 'src/components/molecules/ProductFeatures/components/ProductFeatures';
import { trackProductFeaturesImpression } from 'src/components/molecules/ProductFeatures/tracking/productFeaturesEvents';
import { getProductFeatureItem } from 'src/components/molecules/ProductFeatures/utils/getProductFeatureItem';
import { getTopProductFeatures } from 'src/components/molecules/ProductFeatures/utils/getTopProductFeatures';
import { selectOptions } from 'src/redux/basket/selectors';
import { selectProduct } from 'src/redux/product/selectors';
import { getMinimumOptionsCost } from 'src/redux/product/utils/getMinimumOptionsCost';
import { isMadeToOrder } from 'src/utils/inventoryStatus';

/**
 * View model for the ProductFeatures list. Owns the feature-selection logic
 * (top 3 of the original feature set, plus Made to Order appended uncapped)
 * and the impression tracking, so the presentational component stays pure.
 */
export interface ProductFeaturesViewModel {
  features: ProductFeatureItem[];
  onVisible: (labels: string) => void;
}

export const useProductFeaturesViewModel = (): ProductFeaturesViewModel => {
  const product = useSelector(selectProduct);
  const options = useSelector(selectOptions);

  const { currency } = product.price;
  const minimumOptionsCost = getMinimumOptionsCost({
    currency,
    options,
    personalisable: product.specialFeatures.personalisable,
  });

  const topFeatures = getTopProductFeatures({
    allowsGiftWrap: product.allowsGiftWrap,
    specialFeatures: product.specialFeatures,
  });

  const orderedFeatures = isMadeToOrder(product.inventoryStatus)
    ? [...topFeatures, ProductFeature.MadeToOrder]
    : topFeatures;

  return {
    features: orderedFeatures
      .map((feature) => getProductFeatureItem(feature, { currency, minimumOptionsCost }))
      .filter((item): item is ProductFeatureItem => item !== undefined),
    onVisible: (labels) => trackProductFeaturesImpression(labels),
  };
};
